import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface SkuPhoto {
  id: string;
  sku: string;
  provider_id: string;
  storage_path: string;
  photo_url: string;
  photo_type: string;
  display_order: number;
  is_primary: boolean;
  original_filename: string | null;
  file_size_bytes: number | null;
  width: number | null;
  height: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

interface UseSkuPhotosReturn {
  photos: SkuPhoto[];
  loading: boolean;
  uploading: boolean;
  error: string | null;
  uploadPhoto: (file: File, isPrimary?: boolean) => Promise<SkuPhoto | null>;
  setPrimary: (photoId: string) => Promise<boolean>;
  deletePhoto: (photoId: string) => Promise<boolean>;
  reorderPhotos: (photoIds: string[]) => Promise<boolean>;
  refresh: () => Promise<void>;
  canUploadMore: boolean;
}

const MAX_PHOTOS = 10;

export const useSkuPhotos = (
  sku: string | null,
  providerId: string | null
): UseSkuPhotosReturn => {
  const [photos, setPhotos] = useState<SkuPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load photos for the given SKU and provider
  const loadPhotos = useCallback(async () => {
    if (!sku || !providerId) {
      setPhotos([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Query photos directly from the database (RLS handles access control)
      const { data: photosData, error: dbError } = await supabase
        .from('sku_photos')
        .select('*')
        .eq('sku', sku)
        .eq('provider_id', providerId)
        .is('deleted_at', null)
        .order('is_primary', { ascending: false })
        .order('display_order', { ascending: true });

      if (dbError) {
        throw new Error(dbError.message);
      }

      setPhotos(photosData || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load photos';
      setError(message);
      console.error('Error loading photos:', err);
    } finally {
      setLoading(false);
    }
  }, [sku, providerId]);

  // Upload a new photo
  const uploadPhoto = useCallback(
    async (file: File, isPrimary = false): Promise<SkuPhoto | null> => {
      if (!sku || !providerId) {
        setError('SKU and provider are required');
        return null;
      }

      if (photos.length >= MAX_PHOTOS) {
        setError(`Maximum ${MAX_PHOTOS} photos per SKU reached`);
        return null;
      }

      setUploading(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('sku', sku);
        formData.append('provider_id', providerId);
        formData.append('is_primary', isPrimary.toString());

        const { data, error: uploadError } = await supabase.functions.invoke(
          'sku-photos',
          {
            method: 'POST',
            body: formData,
          }
        );

        if (uploadError) {
          throw new Error(uploadError.message);
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        // Refresh photos list
        await loadPhotos();
        return data?.photo || null;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to upload photo';
        setError(message);
        console.error('Error uploading photo:', err);
        return null;
      } finally {
        setUploading(false);
      }
    },
    [sku, providerId, photos.length, loadPhotos]
  );

  // Set a photo as primary
  const setPrimary = useCallback(
    async (photoId: string): Promise<boolean> => {
      setError(null);

      try {
        const { data, error: updateError } = await supabase.functions.invoke(
          'sku-photos',
          {
            method: 'PATCH',
            body: { photo_id: photoId, is_primary: true },
          }
        );

        if (updateError) {
          throw new Error(updateError.message);
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        // Refresh photos list
        await loadPhotos();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to set primary photo';
        setError(message);
        console.error('Error setting primary photo:', err);
        return false;
      }
    },
    [loadPhotos]
  );

  // Soft delete a photo
  const deletePhoto = useCallback(
    async (photoId: string): Promise<boolean> => {
      setError(null);

      try {
        const { data, error: deleteError } = await supabase.functions.invoke(
          'sku-photos',
          {
            method: 'DELETE',
            body: { photo_id: photoId },
          }
        );

        if (deleteError) {
          throw new Error(deleteError.message);
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        // Refresh photos list
        await loadPhotos();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete photo';
        setError(message);
        console.error('Error deleting photo:', err);
        return false;
      }
    },
    [loadPhotos]
  );

  // Reorder photos by updating display_order
  const reorderPhotos = useCallback(
    async (photoIds: string[]): Promise<boolean> => {
      setError(null);

      try {
        // Update each photo's display_order
        for (let i = 0; i < photoIds.length; i++) {
          const { error: updateError } = await supabase.functions.invoke(
            'sku-photos',
            {
              method: 'PATCH',
              body: { photo_id: photoIds[i], display_order: i },
            }
          );

          if (updateError) {
            throw new Error(updateError.message);
          }
        }

        // Refresh photos list
        await loadPhotos();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to reorder photos';
        setError(message);
        console.error('Error reordering photos:', err);
        return false;
      }
    },
    [loadPhotos]
  );

  // Load photos when SKU or provider changes
  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  return {
    photos,
    loading,
    uploading,
    error,
    uploadPhoto,
    setPrimary,
    deletePhoto,
    reorderPhotos,
    refresh: loadPhotos,
    canUploadMore: photos.length < MAX_PHOTOS,
  };
};
